import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API_BASE_URL } from '../../config';
import { useAuth } from '../../components/shared/AuthContext';
import { PageLayout, Table, Button, DatePicker } from "../../components/ui";
import Loader from '../../components/ui/Loader';
import { ReportHeader, ReportSummaryCard } from "../../components/reports";

const fmt = (n) => (n != null ? Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2 }) : "—");
const fmtDate = (d) => {
  if (!d) return "—";
  const s = typeof d === "string" ? d : new Date(d).toISOString();
  const [y, m, dd] = s.slice(0, 10).split("-");
  return `${dd}-${m}-${y}`;
};

const ForeclosureReport = () => {
  const { data: authData, roleName } = useAuth();
  const branchId = authData?.branchId || null;
  const isSuperAdmin = roleName === "superadmin";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (!isSuperAdmin && branchId) params.branchId = branchId;
      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;

      const res = await axios.get("/api/v2/foreclosure-report", {
        baseURL: API_BASE_URL,
        params,
      });
      const data = Array.isArray(res.data?.data) ? res.data.data : [];
      setRows(data);
    } catch (err) {
      console.error("ForeclosureReport load error:", err);
    }
    setLoading(false);
  }, [isSuperAdmin, branchId, fromDate, toDate]);

  useEffect(() => { loadData(); }, [loadData]);

  const filtered = rows.filter((r) => {
    const q = search.toLowerCase();
    return (
      (r.customer_name      || "").toLowerCase().includes(q) ||
      (r.loan_id            || "").toLowerCase().includes(q) ||
      (r.customer_unique_id || "").toLowerCase().includes(q) ||
      (r.mobile_number      || "").includes(q)
    );
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / rowsPerPage));
  const paginated = filtered.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  const totalForeclosureAmount = filtered.reduce((s, r) => s + Number(r.foreclosure_amount || 0), 0);
  const totalLoanAmount = filtered.reduce((s, r) => s + Number(r.loan_amount || 0), 0);

  const exportCSV = () => {
    const headers = ["#","Customer Name","Customer ID","Loan ID","Mobile","Loan Amount (₹)","Rate of Interest (%)","Tenure (Months)","EMI Date","Foreclosure Date","Amount Collected (₹)","Narration"];
    const csvRows = [
      headers.join(","),
      ...filtered.map((r, i) => [
        i + 1,
        `"${r.customer_name   || ""}"`,
        r.customer_unique_id || "",
        r.loan_id            || "",
        r.mobile_number      || "",
        r.loan_amount        || 0,
        r.rate_of_interest   || 0,
        r.tenure_months      || 0,
        r.emi_date           || "",
        fmtDate(r.foreclosure_date),
        r.foreclosure_amount || 0,
        `"${r.narration      || ""}"`,
      ].join(",")),
    ];
    const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "foreclosure_report.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <PageLayout className="h-auto min-h-[calc(100vh-120px)] overflow-y-auto pb-4">
      <ReportHeader
        className="mb-4"
        filters={
          <>
            {/* From Date */}
            <div className="w-[145px] mt-1">
              <DatePicker
                label="From Date"
                selected={fromDate}
                onChange={(date) => setFromDate(date ? date.toISOString().split('T')[0] : '')}
                placeholder="YYYY-MM-DD"
              />
            </div>

            {/* To Date */}
            <div className="w-[145px] mt-1">
              <DatePicker
                label="To Date"
                selected={toDate}
                onChange={(date) => setToDate(date ? date.toISOString().split('T')[0] : '')}
                placeholder="YYYY-MM-DD"
              />
            </div>

            {/* Apply Button */}
            <div className="flex flex-col justify-end mt-1">
              <Button onClick={loadData} variant="secondary" className="h-[38px]">
                Apply Filters
              </Button>
            </div>

            {/* Search Input */}
            <div className="flex flex-col justify-end min-w-[240px] mt-1">
              <input
                placeholder="Search name / loan ID / customer ID…"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                className="w-full bg-white rounded-lg border border-border-medium hover:border-primary focus:border-primary text-ink-700 h-[38px] text-sm px-4 focus:outline-none transition-all"
              />
            </div>
          </>
        }
        actions={
          <Button
            onClick={exportCSV}
            variant="primary"
            size="sm"
            className="shadow-sm"
          >
            <i className="fa-solid fa-file-csv mr-1.5"></i>
            Export CSV
          </Button>
        }
      />
      {loading && !rows.length && <Loader message="Loading foreclosure data..." />}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <ReportSummaryCard
          label="Total Foreclosed Loans"
          value={filtered.length}
          bg="bg-[#D9F3F7]"
          border="border-[#0891B2]/25"
          accent="text-[#0891B2]"
          icon="fa-file-signature"
        />
        <ReportSummaryCard
          label="Total Original Loan Amount"
          value={`₹${fmt(totalLoanAmount)}`}
          bg="bg-[#DCEBFF]"
          border="border-[#2563EB]/25"
          accent="text-[#2563EB]"
          icon="fa-money-bill-wave"
        />
        <ReportSummaryCard
          label="Total Amount Collected"
          value={`₹${fmt(totalForeclosureAmount)}`}
          bg="bg-[#DDF5E6]"
          border="border-[#16A34A]/25"
          accent="text-[#16A34A]"
          icon="fa-hand-holding-dollar"
        />
      </div>

      {/* Data Table */}
      <div className="flex-grow flex flex-col min-h-0">
        <Table>
          <Table.Head>
            <tr>
              <Table.HeadCell align="center">#</Table.HeadCell>
              <Table.HeadCell align="left">Customer Name</Table.HeadCell>
              <Table.HeadCell align="center">Customer ID</Table.HeadCell>
              <Table.HeadCell align="center">Loan ID</Table.HeadCell>
              <Table.HeadCell align="center">Mobile</Table.HeadCell>
              <Table.HeadCell align="right">Loan Amount</Table.HeadCell>
              <Table.HeadCell align="center">ROI</Table.HeadCell>
              <Table.HeadCell align="center">Tenure</Table.HeadCell>
              <Table.HeadCell align="center">EMI Date</Table.HeadCell>
              <Table.HeadCell align="center">Foreclosure Date</Table.HeadCell>
              <Table.HeadCell align="right">Amount Collected</Table.HeadCell>
              <Table.HeadCell align="left">Narration</Table.HeadCell>
            </tr>
          </Table.Head>
          <Table.Body>
            {paginated.length > 0 ? (
              paginated.map((r, i) => (
                <Table.Row key={r.id || i}>
                  <Table.Cell align="center" className="text-ink-400">{(currentPage - 1) * rowsPerPage + i + 1}</Table.Cell>
                  <Table.Cell align="left" className="font-semibold text-ink-900">{r.customer_name || "—"}</Table.Cell>
                  <Table.Cell align="center" className="font-semibold text-primary">{r.customer_unique_id || "—"}</Table.Cell>
                  <Table.Cell align="center" className="font-semibold text-ink-800">{r.loan_id || "—"}</Table.Cell>
                  <Table.Cell align="center">{r.mobile_number || "—"}</Table.Cell>
                  <Table.Cell align="right" className="font-medium">₹{fmt(r.loan_amount)}</Table.Cell>
                  <Table.Cell align="center">{r.rate_of_interest ? `${r.rate_of_interest}%` : "—"}</Table.Cell>
                  <Table.Cell align="center">{r.tenure_months ? `${r.tenure_months} mo` : "—"}</Table.Cell>
                  <Table.Cell align="center">{r.emi_date || "—"}</Table.Cell>
                  <Table.Cell align="center" className="text-rose-600 font-semibold">{fmtDate(r.foreclosure_date)}</Table.Cell>
                  <Table.Cell align="right" className="font-bold text-emerald-600">₹{fmt(r.foreclosure_amount)}</Table.Cell>
                  <Table.Cell align="left" className="max-w-[150px] truncate" title={r.narration || ""}>{r.narration || "—"}</Table.Cell>
                </Table.Row>
              ))
            ) : (
              <Table.EmptyRow colSpan={12} message="No foreclosure records found." />
            )}
          </Table.Body>
        </Table>

        {/* Pagination Controls */}
        {filtered.length > 0 && (
          <div className="flex items-center justify-between mt-4 bg-white p-3 rounded-xl border border-border-light shadow-sm">
            <div className="flex items-center gap-2 text-xs text-ink-500">
              <span>Rows per page:</span>
              <select
                value={rowsPerPage}
                onChange={(e) => {
                  setRowsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="bg-transparent border border-border-medium rounded px-1.5 py-1 focus:outline-none font-semibold text-ink-700"
              >
                {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <span className="text-xs text-ink-500">
              Showing {paginated.length} of {filtered.length} records
            </span>
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
              >
                First
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
              >
                Last
              </Button>
            </div>
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default ForeclosureReport;
